/**
 * Programmatic control for Google Translate Element.
 * The Google Translate widget creates a hidden <select> element that we
 * manipulate to trigger language changes without showing Google's default UI.
 */

const STORAGE_KEY = 'language';

/** Wait for the Google Translate select element to appear in the DOM */
function waitForGoogleTranslate(maxAttempts = 20): Promise<HTMLSelectElement | null> {
  return new Promise((resolve) => {
    let attempts = 0;
    const interval = setInterval(() => {
      const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
      if (select) {
        clearInterval(interval);
        resolve(select);
      } else if (++attempts >= maxAttempts) {
        clearInterval(interval);
        resolve(null);
      }
    }, 250);
  });
}

/** Programmatically set the Google Translate language */
export async function setLanguage(langCode: string): Promise<void> {
  localStorage.setItem(STORAGE_KEY, langCode);

  if (langCode === 'en') {
    await resetToEnglish();
    return;
  }

  const select = await waitForGoogleTranslate();
  if (!select) return;

  select.value = langCode;
  select.dispatchEvent(new Event('change'));
}

/**
 * Delete the Google Translate "googtrans" cookie across every domain variant.
 *
 * Google writes this cookie not only on the exact host (e.g. one8.esgcaffe.ai)
 * but also on the registrable parent domain (.esgcaffe.ai). Deleting only the
 * host variants leaves the parent-domain cookie alive, so on reload the page
 * gets re-translated — which is why resetting to English failed in production
 * (multi-segment domain) but not on localhost (single segment).
 */
function clearGoogTransCookie(): void {
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  const del = (domainAttr: string) => {
    document.cookie = `googtrans=; ${expires}; path=/;${domainAttr}`;
  };

  // Host-only cookie (no domain attribute)
  del('');

  // Full host + every parent domain, each with and without a leading dot.
  const parts = window.location.hostname.split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    del(` domain=${domain};`);
    del(` domain=.${domain};`);
  }
}

/** Reset Google Translate back to original English */
export async function resetToEnglish(): Promise<void> {
  localStorage.setItem(STORAGE_KEY, 'en');

  // Google Translate stores state in a cookie named "googtrans"
  // Removing it (on every domain variant) + reloading resets translation
  clearGoogTransCookie();

  // Restore the original page text in place by driving Google's hidden combo
  // back to the page language. This avoids a full reload; Google swaps the
  // translated text nodes back to the originals it kept in memory.
  const select = document.querySelector<HTMLSelectElement>('.goog-te-combo')
    ?? (await waitForGoogleTranslate());
  if (!select) return;

  select.value = 'en';
  select.dispatchEvent(new Event('change'));
}

/** Get the currently saved language from localStorage */
export function getSavedLanguage(): string {
  return localStorage.getItem(STORAGE_KEY) || 'en';
}

/** Apply saved language on page load (call after Google Translate initializes) */
export async function applySavedLanguage(): Promise<void> {
  const saved = getSavedLanguage();
  if (saved !== 'en') {
    await setLanguage(saved);
  }
}
